import React from "react";
import PropTypes from "prop-types";

const DEFAULT_SUGGESTIONS = [
  {
    title: "Summarize my ESG reporting workspace.",
    description:
      "Generate an executive overview of reports, evidence, maturity and overall reporting readiness."
  },
  {
    title: "Create an executive ESG readiness summary.",
    description:
      "Prepare a board-ready summary highlighting strengths, risks and priorities."
  },
  {
    title: "What should I work on next?",
    description:
      "Prioritize the next ESG activities based on your current workspace."
  },
  {
    title: "What reports do I currently have?",
    description:
      "Review every ESG report available in your Ecovanta workspace."
  },
  {
    title: "Identify missing ESRS evidence.",
    description:
      "Locate disclosures that still require supporting documentation."
  },
  {
    title: "Review my climate disclosures (ESRS E1).",
    description:
      "Evaluate Scope 1, Scope 2, Scope 3 and climate policy completeness."
  },
  {
    title: "Assess double materiality.",
    description:
      "Review both financial and impact materiality across your ESG topics."
  },
  {
    title: "Compare my ESG maturity with peers.",
    description:
      "Benchmark your ESG readiness against companies in your sector."
  },
  {
    title: "Generate an audit readiness checklist.",
    description:
      "Create a checklist for internal review and external assurance."
  },
  {
    title: "Find reporting gaps before assurance.",
    description:
      "Detect missing disclosures and evidence before the audit."
  },
  {
    title: "Create a new ESG report.",
    description:
      "Start a new ESG reporting workspace with the required structure."
  },
  {
    title: "Explain the CSRD requirements for my company.",
    description:
      "Receive tailored guidance on applicable CSRD and ESRS obligations."
  }
];

export default function EmptyState({
  onPromptSelect,
  prompts = DEFAULT_SUGGESTIONS
}) {
  const suggestions =
    Array.isArray(prompts) && prompts.length
      ? prompts.map((prompt) =>
          typeof prompt === "string"
            ? {
                title: prompt,
                description: ""
              }
            : prompt
        )
      : DEFAULT_SUGGESTIONS;

  return (
    <section
      aria-label="Copilot welcome"
      style={{
        width: "100%",
        maxWidth: 1240,
        margin: "70px auto",
        padding: "0 24px",
        textAlign: "center"
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 72,
          height: 72,
          margin: "0 auto 24px",
          borderRadius: 22,
          display: "grid",
          placeItems: "center",
          background:
            "linear-gradient(135deg,#dff0e9 0%,#eef8f4 100%)",
          border: "1px solid #cfe4dc",
          boxShadow: "0 18px 40px rgba(23,52,46,.08)",
          fontSize: 30
        }}
      >
        ✨
      </div>

      <h2
        style={{
          margin: "0 0 16px",
          color: "#17342e",
          fontSize: 30,
          fontWeight: 700,
          lineHeight: 1.2
        }}
      >
        How can Ecovanta Copilot help?
      </h2>

      <p
        style={{
          maxWidth: 760,
          margin: "0 auto",
          color: "#667f79",
          fontSize: 17,
          lineHeight: 1.8
        }}
      >
        Analyse ESG readiness, identify evidence gaps, review reporting
        workflows, assess materiality, benchmark performance, and generate
        grounded recommendations from your Ecovanta workspace.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 22,
          marginTop: 44,
          textAlign: "left"
        }}
      >
        {suggestions.map((item) => (
          <button
            key={item.title}
            type="button"
            aria-label={item.title}
            onClick={() => onPromptSelect(item.title)}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              minHeight: 170,
              padding: 24,
              borderRadius: 18,
              border: "1px solid #dbe8e3",
              background: "#fff",
              cursor: "pointer",
              textAlign: "left",
              font: "inherit",
              transition: "all .2s ease",
              boxShadow:
                "0 10px 24px rgba(23,52,46,.06)"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform =
                "translateY(-4px)";
              e.currentTarget.style.borderColor =
                "#1d7a63";
              e.currentTarget.style.boxShadow =
                "0 18px 36px rgba(23,52,46,.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform =
                "translateY(0)";
              e.currentTarget.style.borderColor =
                "#dbe8e3";
              e.currentTarget.style.boxShadow =
                "0 10px 24px rgba(23,52,46,.06)";
            }}
          >
            <div>
              <h3
                style={{
                  margin: 0,
                  color: "#17342e",
                  fontSize: 19,
                  fontWeight: 700,
                  lineHeight: 1.4
                }}
              >
                {item.title}
              </h3>

              {item.description && (
                <p
                  style={{
                    marginTop: 14,
                    color: "#647b74",
                    fontSize: 15,
                    lineHeight: 1.6
                  }}
                >
                  {item.description}
                </p>
              )}
            </div>

            <div
              style={{
                marginTop: 22,
                color: "#1d7a63",
                fontSize: 14,
                fontWeight: 700
              }}
            >
              Ask Copilot →
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

EmptyState.propTypes = {
  onPromptSelect: PropTypes.func.isRequired,
  prompts: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        description: PropTypes.string
      })
    ])
  )
};